import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { DeltaChip } from '../DeltaChip';
import { ReportCard, SectionLabel } from '../ReportCard';
import type { PerformanceReport } from './data/api';
import { compactCurrency, count, formatKpi, percent } from './data/format';

interface StatCardProps {
  label: string;
  value: string;
  caption: string;
  delta?: number | null;
  neutralDelta?: boolean;
}

function StatCard({ label, value, caption, delta, neutralDelta }: StatCardProps): ReactNode {
  return (
    <ReportCard>
      <SectionLabel>{label}</SectionLabel>
      <Typography sx={{ fontSize: 40, fontWeight: 300, color: 'text.primary' }}>{value}</Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {delta !== undefined && delta !== null && (
          <DeltaChip value={delta} {...(neutralDelta ? { sentiment: 'neutral' as const } : {})} />
        )}
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{caption}</Typography>
      </Box>
    </ReportCard>
  );
}

export function KpiRow({ report }: { report: PerformanceReport }): ReactNode {
  const { periods, overview, kpi, currencySymbol } = report;
  const { current, priorPeriod } = periods;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(2, minmax(0, 1fr))',
          xl: 'repeat(5, minmax(0, 1fr))',
        },
        gap: 3,
      }}
    >
      <StatCard
        label="Spend"
        value={compactCurrency(current.spend, currencySymbol)}
        caption="vs previous period"
        delta={periods.spendVsPriorPeriod}
        neutralDelta
      />
      <StatCard
        label={kpi.label}
        value={formatKpi(current.kpiValue, kpi, currencySymbol)}
        caption={`vs previous period · ${percent(periods.kpiVsPriorYear, 1)} vs last year`}
        delta={periods.kpiVsPriorPeriod}
      />
      <StatCard
        label="CPM"
        value={compactCurrency(overview.cpm, currencySymbol)}
        caption="blended across networks"
      />
      <StatCard
        label="Active creatives"
        value={count(current.activeCreatives)}
        caption={`vs ${count(priorPeriod.activeCreatives)} in previous period`}
      />
      <StatCard
        label="Top performers"
        value={percent(current.topPerformerShare, 1)}
        caption={`of creatives beating account ${kpi.label} · ${percent(priorPeriod.topPerformerShare, 1)} in previous period`}
      />
    </Box>
  );
}
